import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, ChevronRight } from 'lucide-react';

import {
  apiClient,
  type ApiError,
  type DocumentTemplateCategory,
  type CreateDocumentTemplateCategoryInput,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field, TextInput } from '@/components/clients/form-primitives';
import { GlassSelect } from '@/components/ui/glass-select';
import { ConfigModal } from './config-modal';
import { ListToolbar } from './list-toolbar';

interface FormState {
  name: string;
  description: string;
  parentId: string;
  sortOrder: number;
}

const EMPTY: FormState = {
  name: '',
  description: '',
  parentId: '',
  sortOrder: 0,
};

const QUERY_KEY = ['document-template-categories'];

export function DocumentCategoriesPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentTemplateCategory | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: items = [], isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.listDocumentTemplateCategories(),
  });

  const roots = items.filter((i) => !i.parentId);
  const children = (parentId: string) =>
    items.filter((i) => i.parentId === parentId);

  const filtered = search.trim()
    ? items.filter((i) =>
        i.name.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : null;

  const createMutation = useMutation({
    mutationFn: (input: CreateDocumentTemplateCategoryInput) =>
      apiClient.createDocumentTemplateCategory(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setModalOpen(false);
    },
    onError: (e: ApiError) => setFormError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateDocumentTemplateCategoryInput }) =>
      apiClient.updateDocumentTemplateCategory(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setModalOpen(false);
    },
    onError: (e: ApiError) => setFormError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteDocumentTemplateCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (e: ApiError) => window.alert(e.message),
  });

  function openNew(parentId?: string) {
    setEditing(null);
    setForm({ ...EMPTY, parentId: parentId ?? '', sortOrder: items.length });
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(item: DocumentTemplateCategory) {
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description ?? '',
      parentId: item.parentId ?? '',
      sortOrder: item.sortOrder,
    });
    setFormError(null);
    setModalOpen(true);
  }

  function handleDelete(item: DocumentTemplateCategory) {
    const hasChildren = items.some((i) => i.parentId === item.id);
    if (hasChildren) {
      window.alert(
        'Esta categoria possui subcategorias. Remova-as antes de excluir.',
      );
      return;
    }
    if (!window.confirm(`Excluir a categoria "${item.name}"?`)) return;
    deleteMutation.mutate(item.id);
  }

  function handleSubmit() {
    if (!form.name.trim()) {
      setFormError('Nome obrigatório');
      return;
    }
    setFormError(null);
    const input: CreateDocumentTemplateCategoryInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      parentId: form.parentId || null,
      sortOrder: form.sortOrder,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, input });
    } else {
      createMutation.mutate(input);
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  function renderRow(item: DocumentTemplateCategory, indent = false) {
    return (
      <tr
        key={item.id}
        className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
      >
        <td className="px-4 py-2">
          {indent && (
            <span className="mr-1 inline-block w-4 text-[var(--fg-muted)]">
              <ChevronRight className="h-3 w-3 inline" />
            </span>
          )}
          <button
            type="button"
            onClick={() => openEdit(item)}
            className="text-left font-medium text-[var(--fg)] hover:text-[var(--accent)]"
          >
            {item.name}
          </button>
          {item.description && (
            <p className="text-xs text-[var(--fg-muted)] truncate max-w-xs">
              {item.description}
            </p>
          )}
        </td>
        <td className="px-4 py-2 text-[var(--fg-muted)] text-xs">
          {item.parentId ? 'Subcategoria' : 'Categoria'}
        </td>
        <td className="px-4 py-2 text-[var(--fg-muted)] text-sm">
          {item.sortOrder}
        </td>
        <td className="px-4 py-2 text-right">
          {!indent && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openNew(item.id)}
              className="mr-1"
            >
              + Sub
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(item)}
          >
            Excluir
          </Button>
        </td>
      </tr>
    );
  }

  const displayItems = filtered ?? roots;

  return (
    <div className="space-y-6">
      <ListToolbar
        title="Categorias de modelos"
        description="Organização hierárquica dos modelos de documento (até 2 níveis)."
        newLabel="Nova categoria"
        onNew={() => openNew()}
        search={search}
        onSearchChange={setSearch}
      />

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {isLoading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando…
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">
            {(error as ApiError).message}
          </p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <FolderOpen className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhuma categoria. Clique em{' '}
              <strong>Nova categoria</strong>.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Nome</th>
                <th className="px-4 py-2 font-medium">Tipo</th>
                <th className="px-4 py-2 font-medium">Ordem</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered
                ? displayItems.map((item) => renderRow(item))
                : roots.map((root) => (
                    <>
                      {renderRow(root)}
                      {children(root.id).map((child) =>
                        renderRow(child, true),
                      )}
                    </>
                  ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar categoria' : 'Nova categoria'}
        description="Categoria de classificação para modelos de documento."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {formError}
            </p>
          )}
          <Field label="Nome" required>
            <TextInput
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              placeholder="Ex: Contratos"
              maxLength={200}
            />
          </Field>
          <Field label="Descrição">
            <TextInput
              value={form.description}
              onChange={(e) =>
                setForm((s) => ({ ...s, description: e.target.value }))
              }
              placeholder="Opcional"
              maxLength={200}
            />
          </Field>
          <Field label="Categoria pai">
            <GlassSelect
              value={form.parentId}
              onChange={(v) => setForm((s) => ({ ...s, parentId: v }))}
              placeholder="Sem categoria pai (raiz)"
              options={roots
                .filter((r) => !editing || r.id !== editing.id)
                .map((r) => ({ value: r.id, label: r.name }))}
            />
          </Field>
          <Field label="Ordem">
            <TextInput
              type="number"
              value={String(form.sortOrder)}
              onChange={(e) =>
                setForm((s) => ({
                  ...s,
                  sortOrder: Number(e.target.value) || 0,
                }))
              }
            />
          </Field>
        </div>
      </ConfigModal>
    </div>
  );
}
